import type { MergeStatus, RunStatus, TaskStatus } from "../types";

export type Status = RunStatus | TaskStatus | MergeStatus;

/**
 * One colour rule across the dashboard: sky = moving, emerald = done,
 * amber = waiting on a human or blocked, violet = needs input,
 * red = failed, neutral = parked.
 */
export type Tone = "sky" | "emerald" | "amber" | "violet" | "red" | "neutral";

export const STATUS_TONE: Record<Status, Tone> = {
  queued: "neutral",
  blocked: "amber",
  launching: "sky",
  running: "sky",
  waiting_for_input: "violet",
  waiting_for_approval: "amber",
  waiting_for_human: "violet",
  completed: "emerald",
  failed: "red",
  cancelled: "neutral",
  expired: "red",
  open: "sky",
  waiting: "neutral",
  ready: "amber",
  approved: "emerald",
  held: "violet",
  merging: "sky",
  merged: "emerald",
};

const STATUS_LABEL: Partial<Record<Status, string>> = {
  open: "On the line",
  waiting_for_input: "Needs input",
  waiting_for_approval: "Needs approval",
  waiting_for_human: "Waiting on you",
  ready: "Ready to merge",
  held: "Held for release",
};

export const PULSING: ReadonlySet<Status> = new Set(["running", "launching", "merging", "open"]);

export function statusLabel(status: Status): string {
  const label = STATUS_LABEL[status] ?? status.replace(/_/g, " ");
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export const TONE_BADGE: Record<Tone, string> = {
  sky: "border-sky-500/20 bg-sky-500/10 text-sky-700 dark:text-sky-300",
  emerald: "border-emerald-500/20 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  amber: "border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  violet: "border-violet-500/20 bg-violet-500/10 text-violet-700 dark:text-violet-300",
  red: "border-red-500/20 bg-red-500/10 text-red-700 dark:text-red-300",
  neutral: "border-border bg-muted text-muted-foreground",
};

export const TONE_DOT: Record<Tone, string> = {
  sky: "bg-sky-500",
  emerald: "bg-emerald-500",
  amber: "bg-amber-500",
  violet: "bg-violet-500",
  red: "bg-red-500",
  neutral: "bg-muted-foreground/50",
};

export const TONE_TEXT: Record<Tone, string> = {
  sky: "text-sky-600 dark:text-sky-400",
  emerald: "text-emerald-600 dark:text-emerald-400",
  amber: "text-amber-600 dark:text-amber-400",
  violet: "text-violet-600 dark:text-violet-400",
  red: "text-red-600 dark:text-red-400",
  neutral: "text-muted-foreground",
};
